import {
  AppstoreOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MenuOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { Button, Input, Layout, Menu, type MenuProps, Popover, Spin, Typography } from 'antd';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { defaultAppRoute, navigateToAppPath } from '../api/app-navigation.js';
import type { AuthUser } from '../api/auth-provider.js';
import { http } from '../api/client.js';
import { canReadCrmSecuritySettings, isCrmSecurityAdmin } from '../api/crm-permissions.js';
import { useCrmMode } from '../api/use-crm-mode.js';
import { AppDownloadButton } from '../components/AppDownloadButton.js';
import { CrestLogo } from '../components/CrestBrand.js';
import { CrmModeSwitcher } from '../components/CrmModeSwitcher.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { FeedbackButton } from '../components/FeedbackButton.js';
import { NotificationBell } from '../components/NotificationBell.js';
import { PwaInstallButton } from '../components/PwaInstallButton.js';
import { RoleAvatarSwitcher } from '../components/RoleAvatarSwitcher.js';
import { SipPhone } from '../components/SipPhone.js';
import { tx } from '../i18n/translate.js';
import { Icon } from '../icons/index.js';
import { isDynamicImportLoadError } from '../lib/stale-chunk-reload.js';
import { useIsMobile } from '../utils/use-is-mobile.js';
import { AppSwitcher } from './AppSwitcher.js';
import { filterRetiredPlatformNodes, filterSitemapByAvailability } from './sitemap-availability.js';
import { findBestAppSitemapPath, findBestSitemapPath } from './sitemap-path.js';
import { usePortalSitemaps } from './use-portal-sitemaps.js';

const { Header, Sider, Content } = Layout;
const MENU_ICON_SIZE = 18;
const MENU_ICON_STROKE = 1.9;
const ALWAYS_AVAILABLE = () => true;
const NEVER_DELEGATED = () => false;

export interface SitemapNode {
  id: string;
  type: 'group' | 'entity' | 'view' | 'custom';
  displayNameZh: string;
  displayNameEn: string;
  icon: string | null;
  entityName: string | null;
  customUrl: string | null;
  portalRoot?: boolean;
  sourceAppName?: string;
  children: SitemapNode[];
}

interface SitemapState {
  appName: string | null;
  items: SitemapNode[];
  loaded: boolean;
  failed: boolean;
}

const menuIconProps = { size: MENU_ICON_SIZE, strokeWidth: MENU_ICON_STROKE };

const ICON_MAP: Record<string, React.ReactNode> = {
  account: <Icon.Account {...menuIconProps} />,
  contact: <Icon.Contact {...menuIconProps} />,
  lead: <Icon.Lead {...menuIconProps} />,
  opportunity: <Icon.Opportunity {...menuIconProps} />,
  quote: <Icon.Quote {...menuIconProps} />,
  salesorder: <Icon.SalesOrder {...menuIconProps} />,
  iws_sinosure: <Icon.Sinosure {...menuIconProps} />,
  iws_deliverproject: <Icon.ProjectDelivery {...menuIconProps} />,
  inbox: <Icon.Inbox {...menuIconProps} />,
  approval: <Icon.ApprovalConfig {...menuIconProps} />,
  bell: <Icon.Bell {...menuIconProps} />,
  robot: <Icon.AI size={MENU_ICON_SIZE} />,
  app: <Icon.AppCenter {...menuIconProps} />,
  appcenter: <Icon.AppCenter {...menuIconProps} />,
  apartment: <Icon.BusinessUnit {...menuIconProps} />,
  organization: <Icon.Organization {...menuIconProps} />,
  businessunit: <Icon.BusinessUnit {...menuIconProps} />,
  user: <Icon.UserManagement {...menuIconProps} />,
  users: <Icon.UserManagement {...menuIconProps} />,
  role: <Icon.RolePermission {...menuIconProps} />,
  permission: <Icon.RolePermission {...menuIconProps} />,
  shield: <Icon.RolePermission {...menuIconProps} />,
  security: <Icon.FieldSecurity {...menuIconProps} />,
  audit: <Icon.AuditLog {...menuIconProps} />,
  notification: <Icon.NotificationHistory {...menuIconProps} />,
  integration: <Icon.IntegrationLog {...menuIconProps} />,
  agent: <Icon.AgentConfig {...menuIconProps} />,
  settings: <Icon.ParameterConfig {...menuIconProps} />,
  config: <Icon.ParameterConfig {...menuIconProps} />,
  pricing: <Icon.Pricing {...menuIconProps} />,
  finance: <Icon.Pricing {...menuIconProps} />,
  'trending-up': <Icon.TrendingUp {...menuIconProps} />,
  analytics: <Icon.TrendingUp {...menuIconProps} />,
  'bar-chart': <Icon.TrendingUp {...menuIconProps} />,
  report: <Icon.TrendingUp {...menuIconProps} />,
  reporting: <Icon.TrendingUp {...menuIconProps} />,
  phone: <Icon.Phone {...menuIconProps} />,
  delivery: <Icon.Delivery {...menuIconProps} />,
  funnel: <Icon.SalesFunnel {...menuIconProps} />,
  product: <Icon.ProductCatalog {...menuIconProps} />,
  'reporting-my': <Icon.MyReports {...menuIconProps} />,
  'reporting-coverage': <Icon.ReportCoverage {...menuIconProps} />,
  'reporting-intelligence': <Icon.ReportIntelligence {...menuIconProps} />,
  'reporting-briefings': <Icon.ReportBriefings {...menuIconProps} />,
  'reporting-settings': <Icon.ReportSettings {...menuIconProps} />,
  database: <Icon.Database {...menuIconProps} />,
  history: <Icon.History {...menuIconProps} />,
  flag: <Icon.Lead {...menuIconProps} />,
  'file-text': <Icon.Quote {...menuIconProps} />,
  'map-pin': <Icon.MapPin {...menuIconProps} />,
  tool: <Icon.Settings2 {...menuIconProps} />,
  message: <Icon.Message {...menuIconProps} />,
  wifi: <Icon.Network {...menuIconProps} />,
  workflow: <Icon.Network {...menuIconProps} />,
};

interface RouteErrorBoundaryProps {
  children: ReactNode;
  isEn: boolean;
  resetKey: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  override state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AppLayout] route render failed', error, errorInfo);
  }

  override componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (!this.state.error) return this.props.children;

    const isChunkError = isDynamicImportLoadError(this.state.error);
    const copy = this.props.isEn
      ? {
          title: isChunkError ? 'Page update required' : "Page couldn't load",
          description: isChunkError
            ? 'This page was updated while you were using CREST. Refresh to load the latest page; the assistant window will stay available until you refresh.'
            : 'The current page ran into a problem. You can retry this page or keep using the assistant.',
          retry: 'Retry',
          refresh: 'Refresh',
        }
      : {
          title: isChunkError ? '页面需要刷新后继续' : '页面加载失败',
          description: isChunkError
            ? '你使用 CREST 期间页面版本已更新。请刷新以加载最新页面。'
            : '当前页面遇到问题。你可以重试当前页面或刷新页面。',
          retry: '重试',
          refresh: '刷新页面',
        };

    return (
      <div
        role="alert"
        style={{
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 24,
            boxShadow: '0 12px 36px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {copy.title}
          </Typography.Title>
          <Typography.Paragraph type="secondary">{copy.description}</Typography.Paragraph>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!isChunkError && <Button onClick={() => this.setState({ error: null })}>{copy.retry}</Button>}
            <Button type="primary" onClick={() => window.location.reload()}>
              {copy.refresh}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * 隐藏没有可见子节点的 group（后端 sitemap 已按 priv/role 过滤叶子节点，
 * 但可能留下空 group — 例: 销售员看不到 iws_sinosure 但"销售漏斗"组本身可见）
 */
function pruneEmptyGroups(nodes: SitemapNode[]): SitemapNode[] {
  return nodes.reduce<SitemapNode[]>((acc, node) => {
    const children = pruneEmptyGroups(node.children);
    // group (有子节点定义) 内若所有子都被剔除 → 整个 group 隐藏
    if (node.children.length > 0 && children.length === 0) return acc;
    acc.push({ ...node, children });
    return acc;
  }, []);
}

function nodeToMenuItem(node: SitemapNode, isEn: boolean): NonNullable<MenuProps['items']>[number] {
  const iconKey = node.entityName ?? node.icon ?? '';

  const resolveIcon = () => {
    if (iconKey && ICON_MAP[iconKey]) {
      return ICON_MAP[iconKey];
    }
    const label = `${node.displayNameEn || ''} ${node.displayNameZh || ''}`;
    const url = node.customUrl || '';
    const entity = node.entityName || '';
    const text = `${label} ${url} ${entity}`;
    if (/App Center|Application Center|应用中心/i.test(text)) {
      return <Icon.AppCenter {...menuIconProps} />;
    }
    if (/Organization|组织架构|Business Unit|业务部门/i.test(text)) {
      return <Icon.Organization {...menuIconProps} />;
    }
    if (/User Management|用户管理|systemuser/i.test(text)) {
      return <Icon.UserManagement {...menuIconProps} />;
    }
    if (/Role|Permission|角色|权限/i.test(text)) {
      return <Icon.RolePermission {...menuIconProps} />;
    }
    if (/Field Security|字段安全/i.test(text)) {
      return <Icon.FieldSecurity {...menuIconProps} />;
    }
    if (/Audit|审计/i.test(text)) {
      return <Icon.AuditLog {...menuIconProps} />;
    }
    if (
      /Notification (History|Management)|Mail Notifications|Email Configuration|通知(历史|管理)|邮件通知|邮箱配置/i.test(
        text,
      )
    ) {
      return <Icon.NotificationHistory {...menuIconProps} />;
    }
    if (/Call Settings|通话配置|SIP|拨号/i.test(text)) {
      return <Icon.Phone {...menuIconProps} />;
    }
    if (/Integration Log|集成日志/i.test(text)) {
      return <Icon.IntegrationLog {...menuIconProps} />;
    }
    if (/Agent|智能体/i.test(text)) {
      return <Icon.AgentConfig {...menuIconProps} />;
    }
    if (/Approval Config|Approval Settings|审批配置|审批设置|审批人员设置/i.test(text)) {
      return <Icon.ApprovalConfig {...menuIconProps} />;
    }
    if (/Parameter|参数配置/i.test(text)) {
      return <Icon.ParameterConfig {...menuIconProps} />;
    }
    if (/Pricing|Quote Management|价格管理|报价管理/i.test(text)) {
      return <Icon.Pricing {...menuIconProps} />;
    }
    if (/Collection|Collections|Finance|催收|财务/i.test(text)) {
      return <Icon.Pricing {...menuIconProps} />;
    }
    if (/Delivery|项目交付|交付管理/i.test(text)) {
      return <Icon.Delivery {...menuIconProps} />;
    }
    if (/Product|Catalog|产品|应用清单/i.test(text)) {
      return <Icon.ProductCatalog {...menuIconProps} />;
    }
    if (/Dashboard|看板/.test(label)) {
      if (/Team|团队/.test(label)) return <Icon.Contact {...menuIconProps} />;
      if (/My|我的/.test(label)) return <Icon.User {...menuIconProps} />;
      return <Icon.Kanban {...menuIconProps} />;
    }
    if (/Work List|待办/.test(label)) return <Icon.ListChecks {...menuIconProps} />;
    if (/Approval|审批/.test(label)) return <Icon.ApprovalConfig {...menuIconProps} />;
    if (/Notification|通知/.test(label)) return <Icon.Bell {...menuIconProps} />;
    if (/Analysis|Analytics|数据分析|Report|报表|周报/.test(label)) {
      return <Icon.TrendingUp {...menuIconProps} />;
    }
    if (/Customer|客户/.test(label)) return <Icon.Account {...menuIconProps} />;
    if (/Order|订单/.test(label)) return <Icon.SalesOrder {...menuIconProps} />;
    if (/Funnel|漏斗/.test(label)) return <Icon.SalesFunnel {...menuIconProps} />;
    if (/Inbox|收件箱/.test(label)) return <Icon.Inbox {...menuIconProps} />;
    if (/Feedback|用户反馈|问题反馈/.test(text)) return <Icon.Message {...menuIconProps} />;
    if (/运维管理|Operations Management/.test(text)) return <Icon.Settings2 {...menuIconProps} />;
    if (/Settings|配置|设置/.test(label)) return <Icon.Settings2 {...menuIconProps} />;

    return node.type === 'group' ? <Icon.Layout {...menuIconProps} /> : <Icon.Success {...menuIconProps} />;
  };

  const icon = resolveIcon();
  // 若英文环境但 displayNameEn 缺失/空, 回退到中文 (避免菜单空白)
  const label =
    node.entityName === 'lead' && !isEn ? '线索' : isEn ? node.displayNameEn || node.displayNameZh : node.displayNameZh;
  if (node.children.length > 0) {
    return {
      key: node.id,
      icon,
      label,
      children: node.children.map((c) => nodeToMenuItem(c, isEn)),
    };
  }
  return { key: node.id, icon, label };
}

export function filterMenuItems(items: MenuProps['items'], query: string): MenuProps['items'] {
  const keyword = query.trim().toLocaleLowerCase();
  if (!keyword) return items;

  return (items ?? []).flatMap((item) => {
    if (!item || typeof item !== 'object' || !('key' in item)) return [];
    const candidate = item as typeof item & {
      label?: ReactNode;
      children?: MenuProps['items'];
    };
    const label =
      typeof candidate.label === 'string' || typeof candidate.label === 'number'
        ? String(candidate.label).toLocaleLowerCase()
        : '';
    if (label.includes(keyword)) return [item];

    const children = filterMenuItems(candidate.children, keyword);
    return children?.length ? [{ ...candidate, children }] : [];
  });
}

function menuParentKeys(items: MenuProps['items']): string[] {
  return (items ?? []).flatMap((item) => {
    if (!item || typeof item !== 'object' || !('key' in item) || !('children' in item) || !item.children?.length) {
      return [];
    }
    return [String(item.key), ...menuParentKeys(item.children)];
  });
}

export function mergeMenuOpenKeys(current: string[], required: string[]) {
  const next = [...new Set([...current, ...required])];
  return next.length === current.length && next.every((key, index) => key === current[index]) ? current : next;
}

interface AppLayoutProps {
  /** 宿主提供顶部快捷入口，业务内容仍由对应 Remote 挂载。 */
  headerActions?: ReactNode;
  appNameOverride?: string;
  allowedAppNamesOverride?: string[];
  appSwitcherMode?: 'portal' | 'single' | 'hidden';
  appSwitcherAppName?: string;
  appSwitcherLabelZh?: string;
  appSwitcherLabelEn?: string;
  appSwitcherIcon?: string;
  /** 主应用用于隐藏尚未发布子应用的菜单路由。 */
  isNavigationPathAvailable?: (path: string) => boolean;
  /** 主应用用于隐藏完全由未发布子应用承载的应用分组。 */
  isPortalAppAvailable?: (appName: string) => boolean;
  /** 主应用声明由 Federated Remote 承载的 admin 路由，避免套用平台原生管理页门禁。 */
  isDelegatedAdminPath?: (pathname: string) => boolean;
  /** 主应用可提供一次性版本快照中的菜单，确保侧栏与 MF Remote 同版本。 */
  sitemapLoader?: (appName: string) => Promise<SitemapNode[]>;
  /**
   * 响应式侧栏：移动端收起为抽屉，桌面端可收起为 68px 图标栏。
   * 默认关闭，由唯一 Portal Shell 或需要该导航形态的宿主显式启用。
   */
  responsiveSider?: boolean;
}

export function AppLayout({
  headerActions,
  appNameOverride,
  allowedAppNamesOverride,
  appSwitcherMode = 'portal',
  appSwitcherAppName,
  appSwitcherLabelZh,
  appSwitcherLabelEn,
  appSwitcherIcon,
  isNavigationPathAvailable = ALWAYS_AVAILABLE,
  isPortalAppAvailable = ALWAYS_AVAILABLE,
  isDelegatedAdminPath = NEVER_DELEGATED,
  sitemapLoader,
  responsiveSider = false,
}: AppLayoutProps) {
  const isMobile = useIsMobile();
  const [isFullscreen, setIsFullscreen] = useState(
    () => typeof document !== 'undefined' && Boolean(document.fullscreenElement),
  );
  const [menuFullscreenHintOpen, setMenuFullscreenHintOpen] = useState(false);
  const [bottomFullscreenHintOpen, setBottomFullscreenHintOpen] = useState(false);
  const { data: user } = useGetIdentity<AuthUser>();
  const { appName: routeAppNameParam } = useParams<{ appName: string }>();
  const routeAppName = appNameOverride ?? routeAppNameParam ?? appSwitcherAppName;
  const navigate = useNavigate();
  const location = useLocation();
  const isCrmSettingsRoute = routeAppName === 'crm' && location.pathname.toLowerCase().startsWith('/app/crm/settings');
  const effectiveAppName = isCrmSettingsRoute ? 'crm' : routeAppName;
  const crmMode = useCrmMode();
  const fullscreenSupported =
    typeof document !== 'undefined' &&
    document.fullscreenEnabled &&
    typeof document.documentElement.requestFullscreen === 'function';

  useEffect(() => {
    const syncFullscreenState = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => document.removeEventListener('fullscreenchange', syncFullscreenState);
  }, []);

  const toggleFullscreen = async (source: 'menu' | 'bottom' = 'menu') => {
    if (!fullscreenSupported) {
      if (source === 'bottom') setBottomFullscreenHintOpen(true);
      else setMenuFullscreenHintOpen(true);
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      await document.documentElement.requestFullscreen({
        navigationUI: 'hide',
      });
    } catch {
      // 浏览器可能因权限或系统限制拒绝全屏，保持当前布局即可。
    }
  };
  // 响应式侧栏：移动端使用抽屉，桌面端保留可恢复的图标栏。
  const [siderCollapsed, setSiderCollapsed] = useState<boolean>(
    () => responsiveSider && typeof window !== 'undefined' && window.innerWidth < 992,
  );
  const [menuSearch, setMenuSearch] = useState('');
  // 窄屏下切换路由后自动收起抽屉式侧栏，避免菜单一直盖住内容
  // （依赖 pathname：每次路由变化重跑；条件里读取它以满足 exhaustive-deps）
  useEffect(() => {
    const { pathname } = location;
    if (pathname && responsiveSider && typeof window !== 'undefined' && window.innerWidth < 992) {
      setSiderCollapsed(true);
    }
  }, [location, responsiveSider]);
  const [sitemapState, setSitemapState] = useState<SitemapState>({
    appName: null,
    items: [],
    loaded: false,
    failed: false,
  });
  const { i18n } = useTranslation();
  const isEn = (i18n.language ?? '').toLowerCase().startsWith('en');
  let fullscreenLabel = isEn ? 'Fullscreen' : '全屏显示';
  if (isFullscreen) fullscreenLabel = isEn ? 'Exit fullscreen' : '退出全屏';
  const isSuperAdmin = isCrmSecurityAdmin(user);
  // M-portal 导航形态：门户模式下应用是一级菜单，各应用原一级菜单降为二级
  const portalMenu = appSwitcherMode === 'portal';
  const { portalApps, portalSitemaps } = usePortalSitemaps(portalMenu && !!user, sitemapLoader);

  useEffect(() => {
    if (!effectiveAppName || !user) return;
    const allowedApps = allowedAppNamesOverride ?? user.appNames ?? [];
    if (effectiveAppName === 'admin' && isDelegatedAdminPath(location.pathname)) return;
    if (
      allowedApps.length === 0 ||
      allowedApps.some((appName) => authorizationAppName(appName) === authorizationAppName(effectiveAppName))
    ) {
      return;
    }
    navigateToAppPath(defaultRouteForAuthorizedApp(allowedApps[0] ?? 'admin'), navigate);
  }, [allowedAppNamesOverride, effectiveAppName, isDelegatedAdminPath, location.pathname, navigate, user]);

  // P1-02: Admin App 路由门禁 — 非管理员直访 /app/admin/users 等 URL 时,
  // 即便后端 API 返 403, 前端页面仍会渲染按钮/列表 → 信息泄露.
  // 此处在 layout 层硬拦截: 用户缺少 CRM 安全配置读取权限时
  // 自动重定向到 /app/crm (默认首页), 避免页面 mount.
  useEffect(() => {
    if (effectiveAppName !== 'admin') return;
    if (!user) return; // 等用户上下文加载完
    if (isDelegatedAdminPath(location.pathname)) return;
    if (!canReadCrmSecuritySettings(user)) {
      navigateToAppPath('/app/crm', navigate);
    }
  }, [effectiveAppName, isDelegatedAdminPath, location.pathname, user, navigate]);

  useEffect(() => {
    if (!user || isSuperAdmin) return;
    const path = location.pathname.toLowerCase();
    if (path.includes('/delivery')) {
      navigate('/app/crm', { replace: true });
    }
  }, [isSuperAdmin, location.pathname, navigate, user]);

  useEffect(() => {
    if (!effectiveAppName) return;
    let cancelled = false;
    setSitemapState((prev) =>
      prev.appName === effectiveAppName
        ? { ...prev, failed: false }
        : {
            appName: effectiveAppName,
            items: [],
            loaded: false,
            failed: false,
          },
    );
    const request = sitemapLoader
      ? sitemapLoader(effectiveAppName)
      : http
          .get<SitemapNode[]>(`/apps/${effectiveAppName}/sitemap`, {
            params: { t: Date.now() },
          })
          .then((response) => response.data);
    request
      // 后端已按 RBAC 过滤叶子节点；前端仅修剪空 group
      .then((items) => {
        if (cancelled) return;
        setSitemapState({
          appName: effectiveAppName,
          items: pruneEmptyGroups(items),
          loaded: true,
          failed: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSitemapState((prev) =>
          prev.appName === effectiveAppName && prev.loaded
            ? { ...prev, failed: true }
            : {
                appName: effectiveAppName,
                items: [],
                loaded: true,
                failed: true,
              },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveAppName, sitemapLoader]);

  const sitemap = sitemapState.appName === effectiveAppName ? sitemapState.items : [];
  const sitemapLoaded = sitemapState.appName === effectiveAppName && sitemapState.loaded;
  const sitemapFailed = sitemapState.appName === effectiveAppName && sitemapState.failed;

  /** Platform 只做通用可用性裁剪；业务菜单的名称、层级和可见性由所属应用清单决定。 */
  const decorateAppSitemap = (name: string, items: SitemapNode[]): SitemapNode[] => {
    const available = filterSitemapByAvailability(items, isNavigationPathAvailable);
    const pruned = pruneEmptyGroups(available);
    return name === 'admin' ? filterRetiredPlatformNodes(pruned) : pruned;
  };
  const currentAppSitemap = effectiveAppName ? decorateAppSitemap(effectiveAppName, sitemap) : [];
  const decoratedSitemap = currentAppSitemap;
  /** 门户一级菜单：每个应用一个分组，子项 = 该应用 RBAC 过滤后的原一级菜单 */
  const rawPortalAppTrees = portalMenu
    ? portalApps
        .filter((app) => isPortalAppAvailable(app.name))
        .map((app) => ({
          app,
          nodes:
            app.name === effectiveAppName
              ? currentAppSitemap
              : decorateAppSitemap(app.name, portalSitemaps[app.name] ?? []),
        }))
    : [];
  const promotedPortalNavTrees = rawPortalAppTrees.flatMap(({ app, nodes }) => {
    const promoted = nodes.filter((node) => node.portalRoot === true);
    return promoted.length ? [{ app, nodes: promoted }] : [];
  });
  const portalAppTrees = rawPortalAppTrees.map(({ app, nodes }) => ({
    app,
    nodes: nodes.filter((node) => node.portalRoot !== true),
  }));
  const promotedPortalRoots = promotedPortalNavTrees.flatMap(({ nodes }) => nodes);
  const portalNavTrees = [...promotedPortalNavTrees, ...portalAppTrees];

  const portalApplicationMenuItems: NonNullable<MenuProps['items']> = portalAppTrees
    .filter(({ nodes }) => nodes.length > 0)
    .map(({ app, nodes }) => ({
      key: `app:${app.name}`,
      icon: ICON_MAP[app.icon ?? ''] ?? <AppstoreOutlined />,
      label: isEn ? app.display_name_en : app.display_name_zh,
      children: nodes.map((n) => nodeToMenuItem(n, isEn)),
    }));
  const menuItems: MenuProps['items'] = portalMenu
    ? [...promotedPortalRoots.map((node) => nodeToMenuItem(node, isEn)), ...portalApplicationMenuItems]
    : decoratedSitemap.map((node) => nodeToMenuItem(node, isEn));
  const visibleMenuItems = filterMenuItems(menuItems, menuSearch);

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (portalMenu) {
      // 无子菜单的应用项：点击直达该应用首页
      if (key.startsWith('app:')) {
        navigateToAppPath(`/app/${key.slice(4)}`, navigate);
        return;
      }
      // 菜单节点只按清单提供的 entity/customUrl 导航。
      for (const { app, nodes } of portalNavTrees) {
        const node = flattenSitemap(nodes).find((n) => n.id === key);
        if (!node) continue;
        if (node.entityName) navigateToAppPath(`/app/${app.name}/${node.entityName}`, navigate);
        else if (node.customUrl) navigateToAppPath(node.customUrl, navigate);
        return;
      }
      return;
    }
    const flat = flattenSitemap(decoratedSitemap);
    const node = flat.find((n) => n.id === key);
    if (node?.entityName) navigateToAppPath(`/app/${effectiveAppName}/${node.entityName}`, navigate);
    else if (node?.customUrl) navigateToAppPath(node.customUrl, navigate);
  };

  /**
   * 根据当前 URL 找到匹配的菜单节点 + 其所有父节点 ID。
   *  - URL `/app/crm/account` 或 `/app/crm/account/<id>` → entityName=='account' 的叶子节点
   *  - 自定义 URL 节点：优先匹配完整 pathname + search，再匹配 pathname 前缀
   */
  const selectedPath = findBestAppSitemapPath(
    decoratedSitemap,
    location.pathname,
    location.search,
  );
  const promotedPath = portalMenu
    ? findBestSitemapPath(promotedPortalRoots, location.pathname)
    : null;
  const { selectedKeys, openKeys: routeOpenKeys } = (() => {
    const selected = selectedPath?.at(-1) ?? null;
    const parents = (selectedPath ?? []).slice(0, -1).map((node) => node.id);
    // 门户模式：manifest 声明的 portalRoot 直接作为一级分组，其余节点展开所属应用。
    if (portalMenu) {
      const promotedNode = promotedPath?.at(-1);
      if (promotedNode) {
        return {
          selectedKeys: [promotedNode.id],
          openKeys: (promotedPath ?? []).slice(0, -1).map((node) => node.id),
        };
      }
      return {
        selectedKeys: selected ? [(selected as SitemapNode).id] : [],
        openKeys: effectiveAppName ? [`app:${effectiveAppName}`, ...parents] : parents,
      };
    }
    return {
      selectedKeys: selected ? [(selected as SitemapNode).id] : [],
      openKeys: parents,
    };
  })();
  // 顶栏面包屑：所属应用 / 菜单层级，替代原欢迎语。
  const currentPortalApp = portalApps.find((app) => app.name === effectiveAppName);
  const breadcrumbLabels = ((promotedPath?.length ? promotedPath : (selectedPath ?? [])) as SitemapNode[])
    .map((node) => (isEn ? node.displayNameEn || node.displayNameZh : node.displayNameZh || node.displayNameEn))
    .filter((label): label is string => Boolean(label));
  if (!promotedPath?.length && currentPortalApp) {
    breadcrumbLabels.unshift(isEn ? currentPortalApp.display_name_en : currentPortalApp.display_name_zh);
  }
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(routeOpenKeys);
  const visibleMenuOpenKeys = menuSearch.trim() ? menuParentKeys(visibleMenuItems) : menuOpenKeys;
  const routeOpenKeysSignature = routeOpenKeys.join('\u0000');
  useEffect(() => {
    const requiredKeys = routeOpenKeysSignature ? routeOpenKeysSignature.split('\u0000') : [];
    setMenuOpenKeys((current) => mergeMenuOpenKeys(current, requiredKeys));
  }, [routeOpenKeysSignature]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {responsiveSider && isMobile ? (
        // ── 移动端头部：左 = 菜单(打开侧栏抽屉) / 中 = CREST logo / 右 = 铃铛 + 用户 + 更多(···) ──
        <Header
          className="crest-mobile-header"
          style={{
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Button
            type="text"
            aria-label="menu"
            className="crest-mh-btn"
            icon={<MenuOutlined />}
            onClick={() => setSiderCollapsed(false)}
          />
          <span className="crest-mh-logo">
            <CrestLogo height={22} />
          </span>
          <div className="crest-mh-right">
            {headerActions}
            <FeedbackButton />
            <NotificationBell />
            {/* 用户登录名/头像按钮：常驻显示在铃铛与 ··· 之间 */}
            <RoleAvatarSwitcher />
            <Popover
              trigger="click"
              placement="bottomRight"
              styles={{ body: { padding: 6 } }}
              content={
                <div className="crest-mh-overflow">
                  <SipPhone label={isEn ? 'Call' : '通话'} />
                  <AppDownloadButton label={isEn ? 'Get App' : '下载 App'} />
                  <CrmModeSwitcher label={isEn ? 'Market' : '国内/海外'} />
                  <LanguageSwitcher label={isEn ? 'Language' : '语言切换'} />
                  <Popover
                    trigger="click"
                    placement="left"
                    open={menuFullscreenHintOpen}
                    onOpenChange={(open) => setMenuFullscreenHintOpen(!fullscreenSupported && open)}
                    content="当前浏览器不支持网页全屏，可将页面添加到主屏幕后以应用模式打开。"
                  >
                    <Button
                      type="text"
                      aria-label={isFullscreen ? '退出全屏' : '进入全屏'}
                      title={isFullscreen ? '退出全屏' : '进入全屏，隐藏浏览器工具栏'}
                      icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                      onClick={() => void toggleFullscreen('menu')}
                    >
                      {fullscreenLabel}
                    </Button>
                  </Popover>
                </div>
              }
            >
              <Button type="text" aria-label="more" className="crest-mh-btn" icon={<MoreOutlined />} />
            </Popover>
          </div>
        </Header>
      ) : (
        <Header className="crest-portal-header">
          <div className="crest-portal-header-start">
            <CrestLogo height={20} alt="CREST" />
            {breadcrumbLabels.length ? (
              <nav className="crest-portal-breadcrumb" aria-label={isEn ? 'Breadcrumb' : '当前位置'}>
                {breadcrumbLabels.map((label, index) => (
                  <span
                    key={`${index}:${label}`}
                    aria-current={index === breadcrumbLabels.length - 1 ? 'page' : undefined}
                  >
                    {label}
                  </span>
                ))}
              </nav>
            ) : null}
          </div>
          <div className="crest-portal-header-actions">
            {headerActions}
            <FeedbackButton />
            <NotificationBell />
            <SipPhone />
            <AppDownloadButton />
            <CrmModeSwitcher />
            <LanguageSwitcher />
            <RoleAvatarSwitcher />
          </div>
        </Header>
      )}
      <Layout>
        <Sider
          width={240}
          theme="light"
          className="crest-sider"
          {...(responsiveSider
            ? ({
                collapsible: true,
                breakpoint: 'lg',
                collapsedWidth: isMobile ? 0 : 68,
                collapsed: siderCollapsed,
                onCollapse: setSiderCollapsed,
                trigger: null,
              } as const)
            : {})}
          style={{
            background: 'var(--crest-porcelain)',
            height: 'calc(100vh - 56px)',
            position: 'sticky',
            top: 56,
            overflow: 'hidden',
          }}
        >
          {responsiveSider && !isMobile && (
            <div className="crest-sider-toggle-wrap">
              {!siderCollapsed && (
                <Input
                  allowClear
                  className="crest-sider-menu-search"
                  prefix={<SearchOutlined />}
                  placeholder={isEn ? 'Search menu' : '搜索菜单功能'}
                  aria-label={isEn ? 'Search menu' : '搜索菜单功能'}
                  value={menuSearch}
                  onChange={(event) => setMenuSearch(event.target.value)}
                />
              )}
              <Button
                type="text"
                className="crest-sider-toggle"
                icon={
                  siderCollapsed ? (
                    <PanelLeftOpen size={19} strokeWidth={1.8} />
                  ) : (
                    <PanelLeftClose size={19} strokeWidth={1.8} />
                  )
                }
                aria-label={
                  siderCollapsed ? (isEn ? 'Expand navigation' : '展开侧栏') : isEn ? 'Collapse navigation' : '收起侧栏'
                }
                title={
                  siderCollapsed ? (isEn ? 'Expand navigation' : '展开侧栏') : isEn ? 'Collapse navigation' : '收起侧栏'
                }
                onClick={() => setSiderCollapsed((value) => !value)}
              />
            </div>
          )}
          {/* 上部菜单 — flex 占满剩余空间, 内部可滚动 */}
          <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
            {/* 选中色随主题，来自全局 ConfigProvider 的 Menu token。 */}
            <Menu
              mode="inline"
              className="crest-sider-menu"
              items={visibleMenuItems}
              onClick={onMenuClick}
              selectedKeys={selectedKeys}
              openKeys={visibleMenuOpenKeys}
              onOpenChange={(keys) => setMenuOpenKeys(keys.map(String))}
              style={{ borderRight: 0, background: 'transparent' }}
            />
            {!sitemapLoaded && (
              <Typography.Text type="secondary" style={{ display: 'block', padding: '8px 16px', fontSize: 12 }}>
                {tx('legacy:layout.applayout.sitemapLoading')}
              </Typography.Text>
            )}
            {sitemapFailed && (
              <Typography.Text type="danger" style={{ display: 'block', padding: '8px 16px', fontSize: 12 }}>
                {tx('legacy:layout.applayout.sitemapLoadFailed')}
              </Typography.Text>
            )}
          </div>
          {appSwitcherMode !== 'hidden' && (
            <div
              style={{
                flex: '0 0 auto',
                borderTop: '1px solid var(--crest-rule-soft)',
                padding: portalMenu ? '8px' : '12px 8px',
                background: 'var(--crest-porcelain)',
              }}
            >
              <AppSwitcher
                collapsed={responsiveSider && siderCollapsed}
                mode={appSwitcherMode}
                fixedAppName={appSwitcherAppName}
                fixedLabelZh={appSwitcherLabelZh}
                fixedLabelEn={appSwitcherLabelEn}
                fixedIcon={appSwitcherIcon}
                isAppAvailable={isPortalAppAvailable}
              />
            </div>
          )}
        </Sider>
        {responsiveSider && isMobile && !siderCollapsed && (
          // 抽屉打开时的遮罩：点击非菜单区域自动关闭菜单（装饰性元素，
          // 键盘用户通过菜单项 / 顶部菜单键操作，无需键盘事件）
          // biome-ignore lint/a11y/useKeyWithClickEvents: 装饰性遮罩，仅鼠标/触摸点击关闭
          <div className="crest-sider-mask" aria-hidden="true" onClick={() => setSiderCollapsed(true)} />
        )}
        <Content
          style={{
            padding: '8px 24px 24px',
            background: 'var(--crest-porcelain)',
          }}
        >
          <RouteErrorBoundary isEn={isEn} resetKey={`${crmMode}:${location.pathname}:${location.search}`}>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 240,
                  }}
                >
                  <Spin size="large" />
                </div>
              }
            >
              <Outlet key={crmMode} />
            </Suspense>
          </RouteErrorBoundary>
        </Content>
      </Layout>
      {responsiveSider && isMobile && (
        // ── 移动端底部栏：快捷访问 / 当前应用切换 / 浏览器全屏 ──
        <div className="crest-mobile-appbar">
          <PwaInstallButton />
          <div className="crest-mobile-appbar-center">
            <AppSwitcher
              mode={appSwitcherMode === 'hidden' ? 'single' : appSwitcherMode}
              fixedAppName={appSwitcherAppName}
              fixedLabelZh={appSwitcherLabelZh}
              fixedLabelEn={appSwitcherLabelEn}
              fixedIcon={appSwitcherIcon}
              isAppAvailable={isPortalAppAvailable}
            />
          </div>
          <Popover
            trigger="click"
            placement="topRight"
            open={bottomFullscreenHintOpen}
            onOpenChange={(open) => setBottomFullscreenHintOpen(!fullscreenSupported && open)}
            content="当前浏览器不支持网页全屏，可将页面添加到主屏幕后以应用模式打开。"
          >
            <Button
              type="text"
              className="crest-mobile-appbar-action"
              aria-label={isFullscreen ? '底栏退出全屏' : '底栏进入全屏'}
              title={isFullscreen ? '退出全屏' : '进入全屏，隐藏浏览器工具栏'}
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => void toggleFullscreen('bottom')}
            />
          </Popover>
        </div>
      )}
    </Layout>
  );
}

/** reporting 是历史菜单与路由域，不能参与应用授权比较。 */
function authorizationAppName(appName: string): string {
  const normalized = appName.toLowerCase();
  if (normalized === 'reporting') return 'operations';
  return normalized;
}

function defaultRouteForAuthorizedApp(appName: string): string {
  return defaultAppRoute(authorizationAppName(appName));
}

function flattenSitemap(nodes: SitemapNode[]): SitemapNode[] {
  const result: SitemapNode[] = [];
  for (const n of nodes) {
    result.push(n);
    if (n.children.length) result.push(...flattenSitemap(n.children));
  }
  return result;
}
